import React, { useContext, useEffect, useState } from 'react';
import { Trans } from '@lingui/react';
import { Helmet } from 'react-helmet';
import { Form } from 'react-final-form';
// @ts-ignore
import { GlobalLayoutStyles } from '@qover/styles';
import arrayMutators from 'final-form-arrays';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import cx from 'classnames';
import { Heading3, Heading4 } from '@qover/typography';
import { ContractStatus } from '@qover-private/protos-web/qover/dojo/bike/v1/api';
import {
  Chat,
  ChatWrapper,
  Expandable,
  InputField,
  PrimaryButton,
  Question,
  showChat,
} from '@qover/molecules';
import { focusOnError, Footer, TopBar } from '@qover/organisms';
import { ProcessingLoader } from '@qover/molecules';
import { missingDataValidation } from '../../utils/bikeValidation';
import { required } from '@qover/utils/src/validation';
import { PageContainer } from '../../bike.styles';
import { productService } from '../../services/productService';
import { PICTURE_PARTIAL_CONTRACT_COUNT } from '../../utils/bikeConstants';
import {
  Container,
  InnerContainer,
  InputContainer,
  PageTitle,
  PictureDescription,
  SerialNumberTitle,
} from './bikeMissingData.styles';
import { messages } from './bikeMissingData.messages';
import { BikePicturesDropzone } from './BikePicturesDropzone';
import { poll } from '@qover/utils/src/promise';
import { ZendeskChatContext } from '@qover/contexts';
import Config from '@qover/utils/src/Config';

type BikeMissingDataProps = {
  chat?: any;
  rootUrl: string;
};

const MAX_RETRY_TIME_IN_MS = 5 * 60 * 60 * 1000;
const TIMEOUT_IN_MS = 5 * 1000;

function BikeMissingData(props: BikeMissingDataProps) {
  const Chatprops = props.chat;
  const history = useHistory();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const zendeskContext = useContext(ZendeskChatContext);
  const [mounted, setMounted] = useState(false);
  const { contractId } = useParams<{ contractId: string }>();
  const [partialContract, setPartialContract] = useState<
    ContractStatus | undefined
  >();

  const showUnderwritingQuestions =
    partialContract?.country === 'NL' &&
    !Object.keys(partialContract.uwQuestions ?? {}).length;

  const bikePurchaseDateLessThanThirtyDays =
    partialContract?.bikePurchaseDate &&
    new Date(partialContract?.bikePurchaseDate) >
      new Date(new Date().getTime() - 30 * 24 * 3600 * 1000);

  const hidePicturesUploadForNewBike =
    partialContract?.newBike && bikePurchaseDateLessThanThirtyDays;

  const showPicturesUpload =
    partialContract?.attachments &&
    partialContract?.attachments?.length !== PICTURE_PARTIAL_CONTRACT_COUNT &&
    !hidePicturesUploadForNewBike;

  const showSerialNumber = !partialContract?.serialNumber;

  useEffect(() => {
    setMounted(true);
    productService
      .getPartialContract(contractId)
      .then(res => {
        setPartialContract(res);
      })
      .catch(err => {
        if (err.response?.status === 404) {
          history.push('/already-qovered');
        }
      });
  }, []);

  async function uploadPictures(
    pictures: File[],
    picturesStorage: Array<{ name: string }>,
  ) {
    const picturesPromises = pictures.map((p: File) =>
      productService.createRessourceSignedUrl(contractId, p.type),
    );

    const results = await Promise.all(picturesPromises);

    const uploadPromises = results.map((storageObject: any, idx) => {
      const formData = new FormData();

      Object.keys(storageObject.fields).forEach(k => {
        formData.append(k, storageObject.fields[k]);
      });

      formData.append('file', pictures[idx]);

      picturesStorage.push({ name: storageObject.fields.key });

      return productService.uploadRessourceToBucket(
        storageObject.url,
        formData,
      );
    });

    await Promise.all(uploadPromises);

    const pollController = poll(
      () => productService.getPicturesUploadStatus(contractId),
      result => result.status !== 'READY',
      MAX_RETRY_TIME_IN_MS,
      TIMEOUT_IN_MS,
    );

    return await pollController.promise;
  }

  function onSubmit(form: any) {
    const { pictures, attachment, ...restForm } = form;
    let picturesStorage: Array<{ name: string }> = [];

    let promise: Promise<string[] | void> = showPicturesUpload
      ? uploadPictures(pictures, picturesStorage)
      : Promise.resolve();

    return promise
      .then(() => {
        return showUnderwritingQuestions || showSerialNumber
          ? productService.updateSoftAmend(contractId, { ...restForm })
          : Promise.resolve();
      })
      .then(() =>
        history.push({
          ...location,
          pathname: `${props.rootUrl}/thank-you`,
          state: {
            firstName: partialContract?.firstName,
          },
        }),
      )
      .catch((err: any) => console.error(err));
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <GlobalLayoutStyles />
      {zendeskContext.initializeZendesk && (
        <ChatWrapper
          marginBottom="10px"
          marginRight="10px"
          idBedrockElement="footer-container"
        >
          {props.chat ? <Chatprops /> : <Chat loadChat={Config.loadChat} />}
        </ChatWrapper>
      )}
      <TopBar showChat={showChat} showLanguageSelector />
      <InnerContainer>
        <PageContainer>
          <Container>
            <Form
              decorators={[
                // @ts-ignore
                focusOnError,
              ]}
              initialValues={partialContract}
              onSubmit={onSubmit}
              mutators={{ ...arrayMutators }}
              validate={missingDataValidation(partialContract?.country)}
            >
              {({ handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit}>
                  <PageTitle>
                    <Trans
                      id="bike.missingData.title"
                      defaults="Bonjour {firstName}"
                      values={{ firstName: partialContract?.firstName ?? '' }}
                    />
                  </PageTitle>
                  {showUnderwritingQuestions && (
                    <section data-test="underwritingQuestions">
                      <Heading3>
                        <Trans id="bike.missingData.uwQuestions" />
                      </Heading3>
                      <Question
                        name="uwQuestions.question1"
                        title={messages.question1.title}
                        containerStyle={{ maxWidth: '560px' }}
                      >
                        <Expandable
                          data-test="bike.missingData.question1.details"
                          summary={messages.question1.summary}
                          description={messages.question1.details}
                        />
                      </Question>
                      <Question
                        name="uwQuestions.question2"
                        title={messages.question2.title}
                        containerStyle={{ maxWidth: '560px' }}
                      />
                      <Question
                        name="uwQuestions.question3"
                        title={messages.question3.title}
                        containerStyle={{ maxWidth: '560px' }}
                      >
                        <Expandable
                          data-test="bike.missingData.question3.details"
                          summary={messages.question3.summary}
                          description={messages.question3.details}
                        />
                      </Question>
                    </section>
                  )}
                  {showSerialNumber && (
                    <section data-test="serialNumber">
                      <SerialNumberTitle
                        className={cx({
                          'margin-top-lg': showUnderwritingQuestions,
                        })}
                      >
                        <Trans id="bike.missingData.serialNumber" />
                      </SerialNumberTitle>
                      <InputContainer>
                        <InputField
                          data-test="bike.missingData.serialNumber"
                          id="bike.missingData.serialNumber"
                          label={
                            <Trans id="bike.missingData.serialNumber.label" />
                          }
                          name="serialNumber"
                          validate={required}
                        />
                      </InputContainer>
                    </section>
                  )}

                  {showPicturesUpload && (
                    <section data-test="picturesUpload">
                      <Heading4>
                        <Trans id="bike.missingData.pictures" />
                      </Heading4>
                      <PictureDescription id="bike.missingData.picturesDescription" />
                      {mounted && <BikePicturesDropzone />}
                    </section>
                  )}
                  <PrimaryButton type="submit" disabled={submitting}>
                    <Trans id="general.send" />
                  </PrimaryButton>
                  {submitting && <ProcessingLoader />}
                </form>
              )}
            </Form>
          </Container>
        </PageContainer>
      </InnerContainer>
      <Footer />
    </>
  );
}

export default BikeMissingData;
